import React from "react";
import moment from "moment";
import clsx from "clsx";
import * as Icon from "react-feather";

import WComponent from "../../../common/WComponent";
import ToolsPageHeader from "../kit/ToolsPageHeader";
import ToolsActivityList from "../kit/ToolsActivityList";
import PlaybookLibraryService from "../../../../services/PlaybookLibraryService";
import PlaybookResultsService from "../../../../services/PlaybookResultsService";
import PlaybookRunService from "../../../../services/PlaybookRunService";
import PlaybookWaitingService from "../../../../services/PlaybookWaitingService";
import UtilityService from "../../../../services/UtilityService";
import AgentRunTable from "../../../playbooks/AgentRunTable";
import AgentRunReport from "../../../playbooks/AgentRunReport";
import AgentFindingCounts from "../../../playbooks/AgentFindingCounts";
import StartPlaybookRunModal from "../../../playbooks/StartPlaybookRunModal";
import PlaybookIdentity from "../../../playbooks/PlaybookIdentity";
import AgentSourceList from "../../../playbooks/AgentSourceList";

import "./playbooks-tool.css";

const HISTORY_PAGE_SIZE = 100;
const REFRESH_WHILE_RUNNING_MILLISECONDS = 5000;

class PlaybooksTool extends WComponent {
	constructor(props) {
		super(props);
		this.state = {
			agents: [],
			latestRuns: [],
			earlierRuns: [],
			historyTotal: 0,
			isLoading: true,
			loadError: null,
			agentToRun: null,
			openRunId: null,
			sourcesByAgentId: {},
			sourceErrorsByAgentId: {},
			openHistoryIds: [],
		};
		this.refreshTimer = null;
	}

	componentDidMount() {
		this.fetchEverything();
	}

	componentWillUnmount() {
		if (this.refreshTimer) clearTimeout(this.refreshTimer);
	}

	fetchEverything = async () => {
		const { clientId } = this.props;
		if (this.refreshTimer) clearTimeout(this.refreshTimer);
		const latestFilters = { ...PlaybookResultsService.defaultResultFilters(), view: "latest" };
		const historyFilters = { ...PlaybookResultsService.defaultResultFilters(), view: "all" };
		try {
			const [agents, latestPage, historyPage] = await Promise.all([
				PlaybookLibraryService.listPlaybooks(),
				PlaybookResultsService.listRuns({ filters: latestFilters, clientId }),
				PlaybookResultsService.listRuns({ filters: historyFilters, clientId, pageSize: HISTORY_PAGE_SIZE }),
			]);
			const latestRunIds = latestPage.runs.map(run => run.id);
			const earlierRuns = historyPage.runs.filter(run => !latestRunIds.includes(run.id));
			await this.update({ agents, latestRuns: latestPage.runs, earlierRuns, historyTotal: historyPage.total, isLoading: false, loadError: null });
			this.fetchSourcesForAgents({ agents });
			const isAnyRunning = latestPage.runs.some(run => !PlaybookRunService.isFinished({ status: run.status }));
			if (isAnyRunning) this.refreshTimer = setTimeout(this.fetchEverything, REFRESH_WHILE_RUNNING_MILLISECONDS);
		} catch (error) {
			await this.update({ isLoading: false, loadError: "This client's playbook results could not be loaded." });
		}
	};

	fetchSourcesForAgents = async ({ agents }) => {
		const { clientId } = this.props;
		if (agents.length === 0) return;
		try {
			const { byPlaybookId } = await PlaybookRunService.checkRunSourcesForPlaybooks({ playbookIds: agents.map(agent => agent.id), clientId });
			const sourcesByAgentId = {};
			agents.forEach(agent => {
				if (byPlaybookId[agent.id]) sourcesByAgentId[agent.id] = byPlaybookId[agent.id].sources;
			});
			await this.update({ sourcesByAgentId, sourceErrorsByAgentId: {} });
		} catch (error) {
			const message = UtilityService.describeRequestError({ error, fallback: "The request failed." });
			const sourceErrorsByAgentId = {};
			agents.forEach(agent => {
				sourceErrorsByAgentId[agent.id] = message;
			});
			await this.update({ sourcesByAgentId: {}, sourceErrorsByAgentId });
		}
	};

	onRunAgent = ({ agent }) => {
		this.update({ agentToRun: agent });
	};

	onCloseRunModal = () => {
		this.update({ agentToRun: null });
	};

	onConnectSource = ({ action }) => {
		const { onOpenConnections, history } = this.props;
		this.update({ agentToRun: null });
		if (action.isOnClientPage) onOpenConnections();
		else history.push(action.path);
	};

	onRunStarted = ({ runId }) => {
		this.update({ agentToRun: null, openRunId: runId });
	};

	onOpenRun = ({ runId }) => {
		this.update({ openRunId: runId });
	};

	onBackToList = async () => {
		await this.update({ openRunId: null, isLoading: true });
		this.fetchEverything();
	};

	renderLatest({ agent, latestRun }) {
		if (!latestRun) {
			return (
				<div className="playbooks-tool__latest playbooks-tool__latest--empty">
					<span className="playbooks-tool__latest-none">Not run on this client yet.</span>
				</div>
			);
		}

		const waiting = PlaybookWaitingService.describeWaitingOn({ run: latestRun });
		const isWaitingOnSomeone = !!waiting;
		const isRunning = !PlaybookRunService.isFinished({ status: latestRun.status });
		const { needsPeriod } = PlaybookRunService.runInputsNeededBy({ agent });
		const periodLabel = PlaybookRunService.periodLabelFor({ periodEnd: latestRun.period_end, isLong: true });
		const missingPeriodText = needsPeriod ? "No period recorded" : "On request";
		const starter = latestRun.run_trigger === "scheduled" ? "automatically" : `by ${PlaybookRunService.shortNameFor({ user: latestRun.started_by_user })}`;
		const { costLabel } = PlaybookRunService.describeRun({ run: latestRun });
		const ranText = [`${isRunning ? "started" : "ran"} ${moment(latestRun.created_at).fromNow()} ${starter}`, costLabel].filter(Boolean).join(" · ");
		const reasonNotRun = PlaybookRunService.reasonRunDidNotRun({ run: latestRun });

		const outcome = PlaybookRunService.describeRunOutcome({ run: latestRun });
		const isDecisionWaiting = isWaitingOnSomeone && latestRun.review_stage === "needs_decisions";
		let actionLabel = "Open report";
		if (isRunning) actionLabel = "Watch progress";
		else if (isDecisionWaiting) actionLabel = "Review findings";

		return (
			<div className="playbooks-tool__latest">
				<div className="playbooks-tool__latest-main">
					<span className="playbooks-tool__latest-heading">
						<span className="playbooks-tool__latest-label">Latest run</span>
						<span>· {ranText}</span>
					</span>
					<div className="playbooks-tool__latest-period">{periodLabel || missingPeriodText}</div>
					<span className={`tk-badge tk-badge--${outcome.tone} playbooks-tool__latest-status`}>
						<span className={`tk-dot tk-dot--${outcome.tone}`} aria-hidden="true" />
						{outcome.label}
					</span>
					{isWaitingOnSomeone && !waiting.isUnassigned && (
						<span className="playbooks-tool__latest-waiting">
							<Icon.Users size={14} aria-hidden="true" />
							{waiting.title}
						</span>
					)}
					{isWaitingOnSomeone && waiting.isUnassigned && (
						<div className="playbooks-tool__nobody">
							<Icon.AlertCircle size={14} className="playbooks-tool__nobody-icon" aria-hidden="true" />
							<div className="playbooks-tool__nobody-text">
								<span className="playbooks-tool__nobody-title">{waiting.title}</span>
								{waiting.detail && <span className="playbooks-tool__nobody-detail">{waiting.detail}</span>}
							</div>
						</div>
					)}
					{reasonNotRun && <span className="playbooks-tool__latest-reason">{reasonNotRun}</span>}
				</div>
				<div className="playbooks-tool__latest-counts">
					{isRunning ? (
						<ToolsActivityList
							isInline
							isWorking
							status={latestRun.status === "queued" ? "Waiting to start…" : "Working…"}
							substatus="This updates by itself."
						/>
					) : (
						<AgentFindingCounts run={latestRun} size="spread" />
					)}
				</div>
				<button
					type="button"
					className={clsx("tk-button", isDecisionWaiting ? "tk-button--primary" : "tk-button--secondary")}
					onClick={() => this.onOpenRun({ runId: latestRun.id })}
				>
					{actionLabel}
					{isDecisionWaiting ? <Icon.ArrowRight size={15} /> : <Icon.ChevronRight size={14} />}
				</button>
			</div>
		);
	}

	onToggleHistory = ({ agentId }) => {
		const { openHistoryIds } = this.state;
		const isOpen = openHistoryIds.includes(agentId);
		this.update({ openHistoryIds: isOpen ? openHistoryIds.filter(id => id !== agentId) : [...openHistoryIds, agentId] });
	};

	renderRunHistory({ agent }) {
		const { earlierRuns, openHistoryIds } = this.state;
		const runs = earlierRuns.filter(run => run.playbook_id === agent.id);
		if (runs.length === 0) return null;
		const isOpen = openHistoryIds.includes(agent.id);
		return (
			<div className="playbooks-tool__history">
				<button type="button" className="playbooks-tool__history-toggle" onClick={() => this.onToggleHistory({ agentId: agent.id })} aria-expanded={isOpen}>
					<Icon.Clock size={16} className="playbooks-tool__history-icon" aria-hidden="true" />
					<span className="playbooks-tool__history-title">Run history</span>
					<span className="playbooks-tool__history-count">
						· {runs.length} previous {runs.length === 1 ? "run" : "runs"}
					</span>
					{isOpen ? (
						<Icon.ChevronUp size={16} className="playbooks-tool__history-chevron" aria-hidden="true" />
					) : (
						<Icon.ChevronDown size={16} className="playbooks-tool__history-chevron" aria-hidden="true" />
					)}
				</button>
				{isOpen && (
					<div className="playbooks-tool__history-runs">
						<AgentRunTable runs={runs} showClient={false} showAgent={false} selectable={false} onOpenRun={this.onOpenRun} />
					</div>
				)}
			</div>
		);
	}

	renderAgentPanel = agent => {
		const { latestRuns, sourcesByAgentId, sourceErrorsByAgentId } = this.state;
		const latestRun = latestRuns.find(run => run.playbook_id === agent.id) || null;
		const sources = sourcesByAgentId[agent.id];
		const sourceError = sourceErrorsByAgentId[agent.id];
		const isFirstRun = !latestRun;

		const runButton = (
			<button type="button" className="tk-button tk-button--secondary tk-button--compact" onClick={() => this.onRunAgent({ agent })}>
				<Icon.Play size={13} />
				{isFirstRun ? "Run" : "Run again"}
			</button>
		);

		return (
			<section key={agent.id} className="playbooks-tool__agent tk-card">
				<PlaybookIdentity agent={agent} action={runButton} isDescriptionWrapped />
				{sources && (
					<div className="playbooks-tool__sources">
						<AgentSourceList sources={sources} isCompact onConnect={this.onConnectSource} />
					</div>
				)}
				{sourceError && (
					<div className="playbooks-tool__sources">
						<span className="playbooks-tool__sources-error" title={sourceError}>
							<span className="tk-dot tk-dot--warning" />
							Couldn't check sources — {sourceError}
						</span>
					</div>
				)}
				{this.renderLatest({ agent, latestRun })}
				{this.renderRunHistory({ agent })}
			</section>
		);
	};

	renderHistoryNote() {
		const { earlierRuns, historyTotal, latestRuns } = this.state;
		const shownCount = earlierRuns.length + latestRuns.length;
		if (historyTotal <= shownCount) return null;
		return <div className="playbooks-tool__history-note">Run history shows the latest {shownCount} of {historyTotal} runs on this client.</div>;
	}

	render() {
		const { clientId, clientName } = this.props;
		const { agents, isLoading, loadError, agentToRun, openRunId } = this.state;

		if (openRunId) {
			return (
				<div className="container">
					<div className="playbooks-tool">
						<AgentRunReport runId={openRunId} onBack={this.onBackToList} backLabel="Back to this client's playbooks" />
					</div>
				</div>
			);
		}

		return (
			<div className="container">
				<div className="playbooks-tool">
					<ToolsPageHeader title="Playbooks" subtitle="Automated checks and actionable results for this client." />
					{isLoading && <div className="tk-empty tk-card">Loading…</div>}
					{loadError && <div className="tk-notice tk-notice--danger">{loadError}</div>}
					{!isLoading && !loadError && agents.map(this.renderAgentPanel)}
					{!isLoading && !loadError && this.renderHistoryNote()}
				</div>
				<StartPlaybookRunModal
					key={agentToRun ? agentToRun.id : "none"}
					open={!!agentToRun}
					agent={agentToRun}
					clientId={clientId}
					clientName={clientName}
					onClose={this.onCloseRunModal}
					onStarted={this.onRunStarted}
					onConnect={this.onConnectSource}
				/>
			</div>
		);
	}
}

export default PlaybooksTool;
